import { buildReportScopePromptSection } from '@process-advisor/process-miner/consultant/aiReportScope';
import { buildSharedConsultantPayload } from '@process-advisor/process-miner/consultant/consultantSharedAiPayload';
export function buildFrameworkAssessmentPrompt({
  framework = 'ITIL',
  scope = 'General',
  processTitle = 'Process',
  issueTypeStr = '',
  metricsHistory = [],
  metricsTotal = null,
  transitionsJSON = [],
  variantsJSON = [],
  sampleItems = [],
  fieldUsage = [],
  statusTypeSummary = null,
  statusTypeMappings = [],
  activityType = null,
  activityContext = null,
  inputPayload = null,
  reportScopeSummary = null,
  sourceSystem = 'jira',
  appVersion = null,
}) {
  const reportScopeText = buildReportScopePromptSection(reportScopeSummary);
  const normalizedSource = String(sourceSystem || appVersion || '').toLowerCase();
  const isServiceNow = normalizedSource.includes('snow') || normalizedSource.includes('servicenow');
  const isJira = normalizedSource.includes('jira') || normalizedSource.includes('forge') || normalizedSource.includes('atlassian');
  const platformName = isServiceNow ? 'ServiceNow' : isJira ? 'Jira / Jira Service Management' : 'the supplied workflow process';
  const platformGuidance = isServiceNow
    ? `This is a ServiceNow process assessment. Use ServiceNow terminology and recommendations: task records, state/lifecycle, assignment group, assigned to, priority, category/subcategory, SLA/task SLA, Flow Designer, Business Rules, Assignment Rules, Predictive Intelligence, Now Assist, Virtual Agent, Performance Analytics, Knowledge, Service Portal/Employee Center, and relevant ITSM practices. Do not recommend Atlassian-only capabilities such as Jira Automation, JQL, Rovo, ScriptRunner, JMWE, or JWT.`
    : isJira
      ? `This is a Jira / Jira Service Management process assessment. Use Jira and JSM terminology and recommendations: issues, workflows, statuses, transitions, assignee, components, labels, request types, queues, SLAs, Jira Automation, Atlassian Rovo where relevant, dashboards, and Marketplace automation/configuration apps where relevant. Do not recommend ServiceNow-only capabilities such as Flow Designer, Business Rules, Now Assist, Predictive Intelligence, Virtual Agent, or Task SLA configuration.`
      : `The source platform is not definitively identified. Assess the process against the selected framework using the supplied case/entity, activity, attribute, and business terminology. Keep recommendations platform-neutral and do not invent Jira, ServiceNow, or other vendor-specific capabilities unless they are explicitly present in the supplied context.`;

  const evidencePayload =
    inputPayload ||
    buildSharedConsultantPayload({
      issueTypeStr,
      metricsHistory,
      metricsTotal,
      transitionsJSON,
      variantsJSON,
      sampleItems,
      fieldUsage,
      statusTypeSummary,
      statusTypeMappings,
      activityType,
      activityContext,
      sourceSystem,
      appVersion,
    });

  const evidenceText = JSON.stringify(evidencePayload, null, 2);

  return `You are an expert IT service management and workflow consultant.

Assess the ${platformName} process \"${processTitle}\" against the ${framework} framework for the scope \"${scope}\".
Ground the assessment in the operational evidence provided. Do not give generic textbook advice. Infer maturity from the evidence.
${platformGuidance}

CANONICAL EVIDENCE RULES
- The JSON evidence payload is the same canonical Advisor evidence payload used across all Advisor AI content. Use it as the single source of truth.
- workload_signals.attribute_usage is the authoritative attribute/workload segmentation signal. Derive sparse/highly-used/classification conclusions from it rather than expecting separate duplicated sections.
- sample_evidence.rows contains random/scoped sample work items after report-scope and privacy rules. Use sample rows only when item-level examples, validation, or data-quality checks are necessary. Do not let sample rows override aggregate process metrics, variants, transitions, SLA signals, or attribute usage.


Return valid JSON only with this exact shape:
{
  "framework": "${framework}",
  "scope": "${scope}",
  "platform": "${platformName}",
  "executive_summary": "...",
  "overall_maturity": {
    "level": "Initial|Emerging|Defined|Managed|Optimized",
    "score": 1,
    "target_score": 2,
    "target_rationale": "<what reaching the target within ~90 days requires>",
    "rationale": "..."
  },
  "domain_assessments": [
    {
      "domain": "...",
      "practice_reference": "<official framework practice/objective, e.g. 'ITIL 4 Practice: Incident Management' or 'COBIT 2019: DSS02 — Managed Service Requests and Incidents'>",
      "maturity_level": "Initial|Emerging|Defined|Managed|Optimized",
      "score": 1,
      "target_score": 2,
      "strengths": ["..."],
      "gaps": ["..."],
      "evidence": ["..."]
    }
  ],
  "priority_gaps": ["..."],
  "recommendations": [
    {
      "title": "...",
      "domain": "<the domain this recommendation advances — must match a domain_assessments entry>",
      "priority": "High|Medium|Low",
      "time_horizon": "0-30 days|30-60 days|60-90 days",
      "recommendation": "...",
      "expected_outcome": "..."
    }
  ],
  "roadmap": {
    "days_0_30": ["..."],
    "days_30_60": ["..."],
    "days_60_90": ["..."]
  }
}

FRAMEWORK FIDELITY RULES
- Every domain must be a real construct of the selected framework, named by its official practice/objective name, with practice_reference populated. A consultant will present this to a framework-literate client — invented domain names destroy credibility.
- If framework is ITIL: assess 5-7 ITIL 4 practices, selected by evidence relevance from: Incident Management, Service Request Management, Problem Management, Change Enablement, Service Desk, Knowledge Management, Service Level Management, Monitoring and Event Management, Continual Improvement. You may add at most one supplementary domain named "Automation & Platform Capability" with practice_reference "Supplementary — platform capability view (not an ITIL practice)".
- If framework is COBIT: domains must be COBIT 2019 governance/management objectives with their IDs, selected by evidence relevance from: DSS01 Managed Operations, DSS02 Managed Service Requests and Incidents, DSS03 Managed Problems, BAI06 Managed IT Changes, APO11 Managed Quality, APO12 Managed Risk, MEA01 Managed Performance and Conformance Monitoring. Format practice_reference as "COBIT 2019: DSS02 — Managed Service Requests and Incidents".
- Do not invent framework terminology. If the evidence cannot support assessing a practice, omit that practice rather than padding the assessment.

MATURITY MODEL RULES
- Use a 1-5 scale: 1 Initial, 2 Emerging, 3 Defined, 4 Managed, 5 Optimized. Scores are integers.
- Set target_score for each domain and overall: the level realistically reachable in ~90 days by executing the roadmap — usually current +1, never more than +2. target_rationale must state what closes the gap.
- Never fabricate strengths. If a domain shows no evidenced strengths, return an empty strengths array — do not pad it.
- The executive_summary must state the overall score, the 90-day target, and the single most material gap.
- Each recommendation's domain field must match a domain_assessments entry so gaps and actions stay linked.

${reportScopeText}
Context:
${issueTypeStr}

Shared operational evidence payload:
${evidenceText}

Rules:
- Keep the assessment practical and evidence-based.
- Prefer concise but specific statements.
- Recommendations must be tailored to the observed workflow patterns and the selected platform.
- Do not mention lack of access to other evidence. Work with what is provided.`;
}
